/**
 * Language Management Vuex Module
 * 语言管理 Vuex 模块
 *
 * Handles multilingual support with:
 * - Per-venue language persistence (each venue saves its own language preference)
 * - Browser language auto-detection (detects user's browser language on first visit)
 * - Default language fallback mechanism
 *
 * 处理多语言支持，包括：
 * - 按餐厅持久化语言（每个餐厅保存自己的语言偏好）
 * - 浏览器语言自动检测（首次访问时检测用户浏览器语言）
 * - 默认语言回退机制
 *
 * localStorage structure / localStorage 结构:
 * - 'mobile_locale_prefs': JSON object { [venueId]: { language: 'fr', currency: 'EUR' } }
 */

import {
  DEFAULT_LANGUAGE,
  DEFAULT_SUPPORTED_LANGUAGES,
  DEFAULT_UI_TRANSLATIONS,
  mergeLanguageSettings,
} from '@/config/defaultSettings'

/**
 * Get per-venue locale preferences from localStorage
 * 从 localStorage 获取按餐厅的地区偏好设置
 *
 * @returns {Object} - All venue locale preferences / 所有餐厅的地区偏好
 */
function getLocalePrefs() {
  try {
    const prefs = localStorage.getItem('mobile_locale_prefs')
    return prefs ? JSON.parse(prefs) : {}
  } catch (e) {
    return {}
  }
}

/**
 * Maximum number of venue locale preferences to store
 * 最多保存的餐厅地区偏好数量
 *
 * When exceeded, the least recently used venue entry is evicted.
 * 超出后，淘汰最久未使用的餐厅条目。
 */
const MAX_VENUE_PREFS = 10

/**
 * Save per-venue locale preference to localStorage
 * 保存按餐厅的地区偏好到 localStorage
 *
 * Each venue entry includes a `_ts` timestamp for LRU eviction.
 * When the number of venues exceeds MAX_VENUE_PREFS, the oldest entry is removed.
 * 每个餐厅条目包含 `_ts` 时间戳用于 LRU 淘汰。
 * 当餐厅数量超过 MAX_VENUE_PREFS 时，移除最旧的条目。
 *
 * @param {string} venueId - Venue ID / 餐厅 ID
 * @param {string} key - Preference key ('language' or 'currency') / 偏好键名
 * @param {string} value - Preference value / 偏好值
 */
function saveLocalePrefs(venueId, key, value) {
  if (!venueId) return
  try {
    const prefs = getLocalePrefs()
    if (!prefs[venueId]) {
      prefs[venueId] = {}
    }
    prefs[venueId][key] = value
    prefs[venueId]._ts = Date.now()

    // Evict oldest entries if exceeding limit / 超出限制时淘汰最旧的条目
    const venueIds = Object.keys(prefs)
    if (venueIds.length > MAX_VENUE_PREFS) {
      // Sort by timestamp ascending, remove oldest until within limit
      // 按时间戳升序排列，移除最旧的直到在限制范围内
      venueIds
        .sort((a, b) => (prefs[a]._ts || 0) - (prefs[b]._ts || 0))
        .slice(0, venueIds.length - MAX_VENUE_PREFS)
        .forEach(id => delete prefs[id])
    }

    localStorage.setItem('mobile_locale_prefs', JSON.stringify(prefs))
  } catch (e) {
    // Silently fail if localStorage is unavailable
    // 如果 localStorage 不可用，静默失败
  }
}

/**
 * Get saved language for a specific venue
 * 获取特定餐厅的已保存语言
 *
 * @param {string} venueId - Venue ID / 餐厅 ID
 * @returns {string|null} - Saved language code or null / 已保存的语言代码或 null
 */
function getSavedLanguage(venueId) {
  if (!venueId) return null
  const prefs = getLocalePrefs()
  return prefs[venueId]?.language || null
}

/**
 * Detect browser's preferred language and match to supported languages
 * 检测浏览器首选语言并匹配到支持的语言
 *
 * Uses navigator.languages (array of preferred languages) with fallback to navigator.language.
 * Matches by primary language code (e.g., 'fr-FR' matches 'fr').
 * 使用 navigator.languages（首选语言数组），回退到 navigator.language。
 * 按主要语言代码匹配（例如 'fr-FR' 匹配 'fr'）。
 *
 * @param {Array} supportedLanguages - List of supported language objects / 支持的语言列表
 * @returns {string|null} - Matched language code or null / 匹配的语言代码或 null
 */
function detectBrowserLanguage(supportedLanguages) {
  if (!supportedLanguages || supportedLanguages.length <= 1) {
    return null
  }

  // Get browser's preferred languages / 获取浏览器首选语言
  const browserLanguages = navigator.languages || [navigator.language || navigator.userLanguage || '']

  for (const browserLang of browserLanguages) {
    if (!browserLang) continue

    // Extract primary language code (e.g., 'fr-FR' -> 'fr', 'zh-CN' -> 'zh')
    // 提取主要语言代码（例如 'fr-FR' -> 'fr'，'zh-CN' -> 'zh'）
    const primaryCode = browserLang.split('-')[0].toLowerCase()

    // Find exact match first / 优先精确匹配
    const exactMatch = supportedLanguages.find(l => l.code.toLowerCase() === browserLang.toLowerCase())
    if (exactMatch) return exactMatch.code

    // Then try primary code match / 然后尝试主要代码匹配
    const primaryMatch = supportedLanguages.find(l => l.code.toLowerCase() === primaryCode)
    if (primaryMatch) return primaryMatch.code
  }

  return null
}

export default {
  namespaced: true,

  state: {
    defaultLanguage: DEFAULT_LANGUAGE, // Default language for fallback / 默认语言（回退用）
    currentLanguage: DEFAULT_LANGUAGE, // Currently selected language / 当前选中的语言
    supportedLanguages: DEFAULT_SUPPORTED_LANGUAGES, // Supported languages list / 支持的语言列表
    uiTranslations: DEFAULT_UI_TRANSLATIONS, // UI text translations for all languages / 所有语言的 UI 文本翻译
  },

  mutations: {
    /**
     * Set current language
     * 设置当前语言
     */
    setCurrentLanguage(state, lang) {
      // Validate if language is in supported list, otherwise use default
      // 验证语言是否在支持列表中，否则使用默认语言
      const isSupported = state.supportedLanguages.length === 0 || state.supportedLanguages.some(l => l.code === lang)
      state.currentLanguage = isSupported ? lang : state.defaultLanguage
    },

    /**
     * Set supported languages list
     * 设置支持的语言列表
     */
    setSupportedLanguages(state, languages) {
      state.supportedLanguages = languages
      // If current language is not in supported list, reset to default
      // 如果当前语言不在支持列表中，重置为默认语言
      if (languages.length > 0 && !languages.some(l => l.code === state.currentLanguage)) {
        state.currentLanguage = state.defaultLanguage
      }
    },

    /**
     * Set UI translations
     * 设置 UI 翻译
     */
    setUiTranslations(state, translations) {
      state.uiTranslations = translations
    },

    /**
     * Set default language
     * 设置默认语言
     */
    setDefaultLanguage(state, lang) {
      state.defaultLanguage = lang
    },

    /**
     * Reset language state (used after order completion or venue change)
     * 重置语言状态（用于订单完成后或切换餐厅时重置）
     */
    resetLanguage(state) {
      state.currentLanguage = state.defaultLanguage
    },

    /**
     * Reset to default settings
     * 重置为默认设置
     */
    resetToDefaults(state) {
      state.defaultLanguage = DEFAULT_LANGUAGE
      state.currentLanguage = DEFAULT_LANGUAGE
      state.supportedLanguages = DEFAULT_SUPPORTED_LANGUAGES
      state.uiTranslations = DEFAULT_UI_TRANSLATIONS
    },
  },

  getters: {
    /**
     * Get UI translation text with default language fallback
     * 获取 UI 翻译文本（带默认语言回退）
     *
     * @param {string} key - Translation key / 翻译 key
     * @param {string} fallback - Final fallback value / 最终回退值
     * @returns {string} Translated text / 翻译后的文本
     */
    t: state => (key, fallback) => {
      const currentLang = state.currentLanguage
      const defaultLang = state.defaultLanguage

      // 1. Try to get translation for current language
      // 1. 尝试获取当前语言的翻译
      const currentTranslation = state.uiTranslations[currentLang]?.[key]
      if (currentTranslation) return currentTranslation

      // 2. Fallback to default language
      // 2. 回退到默认语言
      const defaultTranslation = state.uiTranslations[defaultLang]?.[key]
      if (defaultTranslation) return defaultTranslation

      // 3. Use provided fallback or key itself
      // 3. 使用提供的 fallback 或 key 本身
      return fallback || key
    },

    // Current language getter / 当前语言 getter
    currentLang: state => state.currentLanguage,

    // Default language getter / 默认语言 getter
    defaultLang: state => state.defaultLanguage,

    // Supported languages getter / 支持的语言列表 getter
    languages: state => state.supportedLanguages,

    // Check if multiple languages are supported / 检查是否有多语言支持
    hasMultipleLanguages: state => state.supportedLanguages.length > 1,
  },

  actions: {
    /**
     * Initialize language for a specific venue
     * 为特定餐厅初始化语言
     *
     * Priority / 优先级:
     * 1. Per-venue saved preference from localStorage
     *    按餐厅保存的偏好（localStorage）
     * 2. Browser language auto-detection (if supported by venue)
     *    浏览器语言自动检测（如果餐厅支持该语言）
     * 3. Venue's default language
     *    餐厅的默认语言
     *
     * @param {string} venueId - Venue ID / 餐厅 ID
     */
    initLanguage({ commit, state }, venueId) {
      // 1. Check per-venue saved preference / 检查按餐厅保存的偏好
      const savedLang = getSavedLanguage(venueId)
      if (savedLang) {
        // Validate if saved language is still in supported list
        // 验证保存的语言是否仍在支持列表中
        if (state.supportedLanguages.length === 0 || state.supportedLanguages.some(l => l.code === savedLang)) {
          commit('setCurrentLanguage', savedLang)
          return
        }
      }

      // 2. Auto-detect browser language / 自动检测浏览器语言
      const browserLang = detectBrowserLanguage(state.supportedLanguages)
      if (browserLang) {
        commit('setCurrentLanguage', browserLang)
        // Save the auto-detected language for this venue
        // 保存自动检测到的语言到该餐厅
        saveLocalePrefs(venueId, 'language', browserLang)
        return
      }

      // 3. Use venue's default language / 使用餐厅的默认语言
      commit('setCurrentLanguage', state.defaultLanguage)
    },

    /**
     * Change current language (and save per-venue preference)
     * 切换语言（并保存按餐厅的偏好）
     *
     * @param {Object} payload - { lang, venueId } / 语言代码和餐厅 ID
     */
    changeLanguage({ commit, rootState }, payload) {
      // Support both old format (string) and new format (object)
      // 同时支持旧格式（字符串）和新格式（对象）
      let lang, venueId
      if (typeof payload === 'string') {
        lang = payload
        venueId = rootState.venueId
      } else {
        lang = payload.lang
        venueId = payload.venueId || rootState.venueId
      }

      commit('setCurrentLanguage', lang)
      // Save per-venue preference / 保存按餐厅的偏好
      saveLocalePrefs(venueId, 'language', lang)
    },

    /**
     * Initialize language settings from API data
     * 初始化语言配置（从 API 数据）
     *
     * API settings will be merged with default settings.
     * Then initializes language based on per-venue preference or browser detection.
     * API 设置将与默认设置合并。
     * 然后根据按餐厅的偏好或浏览器检测来初始化语言。
     *
     * @param {Object} payload - { apiSettings, venueId } / API 设置和餐厅 ID
     */
    initLanguageSettings({ commit, dispatch }, payload) {
      // Support both old format (apiSettings only) and new format (object with venueId)
      // 同时支持旧格式（仅 apiSettings）和新格式（包含 venueId 的对象）
      let apiSettings, venueId
      if (payload && typeof payload === 'object' && ('apiSettings' in payload || 'venueId' in payload)) {
        apiSettings = payload.apiSettings
        venueId = payload.venueId
      } else {
        apiSettings = payload
        venueId = null
      }

      // Merge API settings with defaults / 将 API 设置与默认值合并
      const mergedSettings = mergeLanguageSettings(apiSettings)

      // Set default language / 设置默认语言
      commit('setDefaultLanguage', mergedSettings.defaultLanguage)

      // Set supported languages list / 设置支持的语言列表
      commit('setSupportedLanguages', mergedSettings.supportedLanguages)

      // Set UI translations / 设置 UI 翻译
      commit('setUiTranslations', mergedSettings.uiTranslations)

      // Initialize language (per-venue preference -> browser detection -> default)
      // 初始化语言（按餐厅偏好 -> 浏览器检测 -> 默认）
      dispatch('initLanguage', venueId)
    },
  },
}

// Export helper functions for use by other modules
// 导出辅助函数供其他模块使用
export { saveLocalePrefs, getLocalePrefs, getSavedLanguage }
